// report_builder/ReportResults.jsx
// ============================================================
// REPORT RESULTS
// Displays results from a report run.
// Shows summary, per-symbol match counts, expandable match
// list with per-condition pass/fail, and Charts button.
//
// Props:
//   results    — output from runReport() / report_executor
//   onRunAgain — fn() — go back to runner panel
//   onNewRun   — fn() — go back to report list
// ============================================================

import React, { useState } from 'react';
import { createTempWatchlist } from '../api';
import { inspectMatch } from './reportApi';

// ── IST date/time formatter ──────────────────────────────────
function fmtBucket(bucketIst) {
  if (!bucketIst) return '—';
  return bucketIst.slice(0, 16).replace('T', ' ');
}

// ── Excel export ─────────────────────────────────────────────
function exportToExcel(results) {
  const hasExit = (results.results || []).some(r => r.has_exit);

  let rows = [];

  if (!hasExit) {
    // Entry-only export
    const allCols = results.columns
      ? [...(results.columns.candle_cols || []), ...(results.columns.indicator_cols || [])]
      : ['open', 'high', 'low', 'close'];

    rows.push(['Symbol', 'Timeframe', 'Date/Time',
      ...allCols.map(c => COL_LABELS[c] || c)
    ]);

    (results.results || []).forEach(symResult => {
      (symResult.matches || []).forEach(match => {
        const vals = match.values || {};
        rows.push([
          symResult.symbol,
          symResult.timeframe,
          fmtBucket(match.bucket_ist),
          ...allCols.map(col => vals[col] ?? ''),
        ]);
      });
    });
  } else {
    // Trade export
    rows.push([
      'Symbol', 'Entry Date', 'Entry Price',
      'Exit Date', 'Exit Price', 'Exit Type',
      'Candles Held', 'P&L ₹', 'P&L %',
    ]);

    (results.results || []).forEach(symResult => {
      (symResult.closed_trades || []).forEach(trade => {
        rows.push([
          symResult.symbol,
          fmtBucket(trade.entry_bucket_ist),
          trade.entry_price,
          fmtBucket(trade.exit_bucket_ist),
          trade.exit_price,
          trade.exit_type,
          trade.candles_held,
          trade.pnl_absolute,
          trade.pnl_percent,
        ]);
      });
      (symResult.open_trades || []).forEach(trade => {
        rows.push([
          symResult.symbol,
          fmtBucket(trade.entry_bucket_ist),
          trade.entry_price,
          '—', '—', 'OPEN_AT_END',
          trade.candles_held,
          '—', '—',
        ]);
      });
    });
  }

  const csv = rows
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `report_results_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}


// ============================================================
// CONDITION RESULT ROW
// Shows pass/fail for one condition inside an expanded match
// ============================================================

function ConditionResult({ condResult, index }) {
  const connector = condResult.connector;
  const passed    = condResult.result;
  const type      = condResult.type || '';

  return (
    <div style={cr.row}>
      {index > 0 && connector && (
        <span style={{
          ...cr.connector,
          color: connector === 'OR' ? '#e65100' : '#555',
        }}>
          {connector}
        </span>
      )}
      <span style={{ ...cr.badge, ...(passed ? cr.badgePass : cr.badgeFail) }}>
        {passed ? '✓' : '✗'}
      </span>
      <span style={cr.label}>
        Condition {condResult.id}
        {type === 'n_wrapper' && <span style={cr.typeTag}> (any/all)</span>}
        {type === 'cross'     && <span style={cr.typeTag}> (cross)</span>}
      </span>
    </div>
  );
}

const cr = {
  row: {
    display:    'flex',
    alignItems: 'center',
    gap:        '6px',
    padding:    '2px 0',
  },
  connector: {
    fontSize:   '10px',
    fontWeight: 'bold',
    minWidth:   '28px',
    textAlign:  'right',
    color:      '#555',
  },
  badge: {
    width:          '18px',
    height:         '18px',
    borderRadius:   '50%',
    fontSize:       '10px',
    fontWeight:     'bold',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    flexShrink:     0,
  },
  badgePass: {
    backgroundColor: '#e8f5e9',
    color:           '#2e7d32',
  },
  badgeFail: {
    backgroundColor: '#ffebee',
    color:           '#c62828',
  },
  label: {
    fontSize: '11px',
    color:    '#555',
  },
  typeTag: {
    fontSize: '10px',
    color:    '#888',
  },
};


// ============================================================
// SYMBOL RESULT ROW
// One row per symbol — expandable to show match list
// ============================================================

// ── Format number ────────────────────────────────────────────
function fmtNum(val) {
  if (val === null || val === undefined) return '—';
  if (val >= 10000000) return (val / 10000000).toFixed(2) + 'Cr';
  if (val >= 100000)   return (val / 100000).toFixed(2) + 'L';
  return Number(val).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// ── Column label map ─────────────────────────────────────────
const COL_LABELS = {
  open:         'Open',
  high:         'High',
  low:          'Low',
  close:        'Close',
  volume:       'Volume',
  ema_5_close:  'EMA5',
  ema_9_close:  'EMA9',
  ema_20_close: 'EMA20',
  ema_50_close: 'EMA50',
  ema_200_close:'EMA200',
  ema_5_high:   'EMA5H',
  ema_9_high:   'EMA9H',
  ema_20_high:  'EMA20H',
  ema_50_high:  'EMA50H',
  ema_5_low:    'EMA5L',
  ema_9_low:    'EMA9L',
  ema_20_low:   'EMA20L',
  ema_50_low:   'EMA50L',
};

// ============================================================
// R7 — MATCH INSPECTOR (gap 3)
// Pick a match, evaluate every entry condition individually for
// that exact candle. Renders with the existing ConditionResult rows.
// ============================================================

function MatchInspector({ reportId, symbol, timeframe, matches }) {
  const [selIdx, setSelIdx] = useState(matches.length - 1);
  const [busy,   setBusy]   = useState(false);
  const [data,   setData]   = useState(null);
  const [err,    setErr]    = useState(null);

  if (!reportId || !matches?.length) return null;

  const inspect = async () => {
    const m = matches[selIdx] || matches[matches.length - 1];
    setBusy(true); setErr(null); setData(null);
    try {
      const res = await inspectMatch({
        report_id:      reportId,
        symbol:         symbol,
        bucket:         String(m.bucket),
        base_timeframe: timeframe,
      });
      setData(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Inspect failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '8px 12px 8px 36px',
                  borderTop: '1px solid #f0f0f0',
                  background: '#fdfdfd', fontSize: '12px' }}>
      <span style={{ fontWeight: 'bold', color: '#888',
                     marginRight: '8px' }}>🔍 Inspect match:</span>
      <select
        style={{ fontSize: '12px', padding: '2px 4px',
                 marginRight: '8px' }}
        value={selIdx}
        onChange={e => { setSelIdx(Number(e.target.value));
                         setData(null); }}
      >
        {matches.map((m, i) => (
          <option key={i} value={i}>
            {fmtBucket(m.bucket_ist)}
          </option>
        ))}
      </select>
      <button
        style={{ fontSize: '11px', padding: '2px 10px',
                 border: '1px solid #2962ff', color: '#2962ff',
                 background: '#fff', borderRadius: '4px',
                 cursor: 'pointer' }}
        onClick={inspect}
        disabled={busy}
      >
        {busy ? '…' : 'Inspect'}
      </button>
      {err && <span style={{ color: '#c62828',
                             marginLeft: '8px' }}>{err}</span>}
      {data && (
        <div style={{ marginTop: '6px' }}>
          {(data.conditions || []).map((c, i) => (
            <ConditionResult key={i} condResult={c} index={i} />
          ))}
          {(data.conditions || []).filter(c => c.skipped).map((c, i) => (
            <div key={`n${i}`} style={{ fontSize: '11px',
                                        color: '#b26a00',
                                        paddingLeft: '24px' }}>
              Condition {c.id} not evaluated: {c.note}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SymbolResultRow({ symResult, timeframe, reportId }) {
  const [expanded, setExpanded] = useState(false);

  const {
    symbol, matches = [], total_matches, candles_scanned,
    coverage_from_ist, coverage_to_ist, error, truncated,
  } = symResult;
  const hasMatches = total_matches > 0;

  // Get column list from first match
  const firstMatch  = matches[0];
  const columns     = firstMatch?.columns || { candle_cols: [], indicator_cols: [] };
  const allCols     = [...(columns.candle_cols || []), ...(columns.indicator_cols || [])];

  const avgSymPnl = symResult.closed_trades?.length > 0   // ← added here
    ? Math.round(
        symResult.closed_trades.reduce((s, t) => s + (t.pnl_percent || 0), 0)
        / symResult.closed_trades.length * 100
      ) / 100
    : 0;

  return (
    <div style={sr.wrapper}>

      {/* ── Symbol header row ── */}
      <div
        style={{ ...sr.headerRow, ...(hasMatches ? { cursor: 'pointer' } : {}) }}
        onClick={() => hasMatches && setExpanded(v => !v)}
      >
        <span style={sr.expandIcon}>
          {hasMatches ? (expanded ? '▼' : '▶') : '—'}
        </span>
        <span style={sr.symbol}>{symbol}</span>
        <span style={{
          ...sr.matchCount,
          color:      hasMatches ? '#2e7d32' : '#aaa',
          fontWeight: hasMatches ? 'bold' : 'normal',
        }}>
          {hasMatches
            ? symResult.has_exit
              ? `${symResult.total_closed || 0} closed · ${symResult.total_open || 0} open`
              : `${total_matches} match${total_matches > 1 ? 'es' : ''}`
            : 'No matches'}
        </span>
        <span style={sr.scanned}>
          {candles_scanned != null ? `${candles_scanned} candles` : ''}
          {coverage_from_ist && coverage_to_ist
            ? ` · ${String(coverage_from_ist).slice(0, 10)} → ${String(coverage_to_ist).slice(0, 10)}`
            : ''}
        </span>
        {truncated && (
          <span style={sr.truncNote} title="Match/trade list capped at 500 — counts are full">
            capped
          </span>
        )}
        {error && <span style={sr.error}>{error}</span>}
        {hasMatches && !symResult.has_exit && (
          <span style={sr.latestDate}>
            Latest: {fmtBucket(matches[matches.length - 1]?.bucket_ist)}
          </span>
        )}
        {hasMatches && symResult.has_exit && symResult.total_closed > 0 && (
          <span style={{
            ...sr.latestDate,
            color: avgSymPnl > 0 ? '#2e7d32' : '#d32f2f',
            fontWeight: 'bold',
          }}>
            Avg P&L: {avgSymPnl > 0 ? '+' : ''}{avgSymPnl}%
          </span>
        )}
      </div>

      {/* ── Expanded view ── */}
      {expanded && hasMatches && (
        <div>

          {/* ── R7: per-candle condition inspector ── */}
          <MatchInspector
            reportId={reportId}
            symbol={symbol}
            timeframe={timeframe}
            matches={matches}
          />

          {/* ── ENTRY-ONLY VIEW (no exit defined) ── */}
          {!symResult.has_exit && (
            <div style={sr.matchTable}>
              <div style={sr.matchHeader}>
                <span style={sr.colDate}>Date / Time</span>
                {allCols.map(col => (
                  <span key={col} style={sr.colData}>
                    {COL_LABELS[col] || col}
                  </span>
                ))}
              </div>
              {matches.map((match, idx) => {
                const vals = match.values || {};
                return (
                  <div key={idx} style={{
                    ...sr.matchRow,
                    ...(idx % 2 === 0 ? sr.matchRowEven : {}),
                  }}>
                    <span style={sr.colDate}>{fmtBucket(match.bucket_ist)}</span>
                    {allCols.map(col => (
                      <span key={col} style={{
                        ...sr.colData,
                        color:      col === 'close'         ? '#131722'
                                  : col === 'volume'        ? '#888'
                                  : col.startsWith('ema')   ? '#2962ff'
                                  : '#333',
                        fontWeight: col === 'close' ? 'bold' : 'normal',
                      }}>
                        {fmtNum(vals[col])}
                      </span>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── TRADE VIEW (exit/SL defined) ── */}
          {symResult.has_exit && (
            <div style={sr.matchTable}>

              {/* Closed trades */}
              {(symResult.closed_trades || []).length > 0 && (
                <>
                  <div style={sr.tradeGroupLabel}>Closed Trades</div>
                  <div style={sr.matchHeader}>
                    <span style={sr.colDate}>Entry</span>
                    <span style={sr.colData}>Entry ₹</span>
                    {allCols.filter(c => c !== 'open' && c !== 'volume').map(col => (
                      <span key={`e_${col}`} style={sr.colData}>
                        {COL_LABELS[col] || col}
                      </span>
                    ))}
                    <span style={sr.colDate}>Exit</span>
                    <span style={sr.colData}>Exit ₹</span>
                    <span style={sr.colData}>Candles</span>
                    <span style={sr.colData}>P&L ₹</span>
                    <span style={sr.colData}>P&L %</span>
                    <span style={sr.colData}>Action</span>
                    <span style={sr.colType}>Type</span>
                  </div>
                  {(symResult.closed_trades || []).map((trade, idx) => {
                    const eVals = trade.entry_values || {};
                    const isPnlPos = trade.pnl_percent >= 0;
                    return (
                      <div key={idx} style={{
                        ...sr.matchRow,
                        ...(idx % 2 === 0 ? sr.matchRowEven : {}),
                      }}>
                        <span style={sr.colDate}>{fmtBucket(trade.entry_bucket_ist)}</span>
                        <span style={{ ...sr.colData, fontWeight: 'bold' }}>
                          {fmtNum(trade.entry_price)}
                        </span>
                        {allCols.filter(c => c !== 'open' && c !== 'volume').map(col => (
                          <span key={`e_${col}`} style={{
                            ...sr.colData,
                            color: col.startsWith('ema') ? '#2962ff' : '#555',
                            fontSize: '11px',
                          }}>
                            {fmtNum(eVals[col])}
                          </span>
                        ))}
                        <span style={sr.colDate}>{fmtBucket(trade.exit_bucket_ist)}</span>
                        <span style={{ ...sr.colData, fontWeight: 'bold' }}>
                          {fmtNum(trade.exit_price)}
                        </span>
                        <span style={sr.colData}>{trade.candles_held}</span>
                        <span style={{
                          ...sr.colData,
                          color:      isPnlPos ? '#2e7d32' : '#d32f2f',
                          fontWeight: 'bold',
                        }}>
                          {trade.pnl_absolute > 0 ? '+' : ''}{fmtNum(trade.pnl_absolute)}
                        </span>
                        <span style={{
                          ...sr.colData,
                          color:      isPnlPos ? '#2e7d32' : '#d32f2f',
                          fontWeight: 'bold',
                        }}>
                          {trade.pnl_percent > 0 ? '+' : ''}{trade.pnl_percent}%
                        </span>
                        <span style={{
                          ...sr.colData,
                          fontSize:   '11px',
                          fontWeight: 'bold',
                          color: trade.exit_action === 'sell' ? '#d32f2f' : '#2e7d32',
                        }}>
                          {trade.exit_action?.toUpperCase()}
                        </span>
                        <span style={{
                          ...sr.colType,
                          color: trade.exit_type === 'SL_HIT'         ? '#d32f2f'
                               : trade.exit_type === 'CONDITION_EXIT' ? '#2e7d32'
                               : '#888',
                          fontSize: '10px',
                        }}>
                          {trade.exit_type === 'SL_HIT'         ? 'SL'
                         : trade.exit_type === 'CONDITION_EXIT' ? 'Exit'
                         : trade.exit_type}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}

              {/* Open trades */}
              {(symResult.open_trades || []).length > 0 && (
                <>
                  <div style={{ ...sr.tradeGroupLabel, color: '#e65100' }}>
                    Open Trades (no exit found in date range)
                  </div>
                  <div style={sr.matchHeader}>
                    <span style={sr.colDate}>Entry</span>
                    <span style={sr.colData}>Entry ₹</span>
                    <span style={sr.colData}>Candles Held</span>
                    <span style={sr.colData}>Status</span>
                  </div>
                  {(symResult.open_trades || []).map((trade, idx) => (
                    <div key={idx} style={{
                      ...sr.matchRow,
                      background: '#fff8f0',
                    }}>
                      <span style={sr.colDate}>{fmtBucket(trade.entry_bucket_ist)}</span>
                      <span style={{ ...sr.colData, fontWeight: 'bold' }}>
                        {fmtNum(trade.entry_price)}
                      </span>
                      <span style={sr.colData}>{trade.candles_held}</span>
                      <span style={{ ...sr.colData, color: '#e65100', fontWeight: 'bold' }}>
                        Open
                      </span>
                    </div>
                  ))}
                </>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );
}

const sr = {
  wrapper: {
    borderBottom: '1px solid #f0f0f0',
  },
  headerRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        '10px',
    padding:    '8px 12px',
    background: '#fff',
  },
  expandIcon: { fontSize: '10px', color: '#888', width: '12px', flexShrink: 0 },
  symbol:     { fontSize: '13px', fontWeight: 'bold', color: '#131722', width: '110px', flexShrink: 0 },
  matchCount: { fontSize: '12px', width: '100px', flexShrink: 0 },
  scanned:    { fontSize: '11px', color: '#bbb', flexShrink: 0 },
  error:      { fontSize: '11px', color: '#d32f2f' },
  truncNote:  {
    fontSize:     '10px',
    color:        '#1565c0',
    background:   '#e3f2fd',
    border:       '1px solid #90caf9',
    borderRadius: '3px',
    padding:      '0 5px',
    flexShrink:   0,
  },
  latestDate: { fontSize: '11px', color: '#888', marginLeft: 'auto' },

  matchTable: {
    overflowX:   'auto',
    borderTop:   '1px solid #f0f0f0',
    paddingLeft: '36px',
    background:  '#fafafa',
  },
  matchHeader: {
    display:       'flex',
    padding:       '5px 12px',
    fontSize:      '10px',
    fontWeight:    'bold',
    color:         '#888',
    textTransform: 'uppercase',
    borderBottom:  '1px solid #e0e0e0',
    background:    '#f0f0f0',
    gap:           '0px',
  },
  matchRow: {
    display:      'flex',
    padding:      '5px 12px',
    fontSize:     '12px',
    borderBottom: '1px solid #f5f5f5',
    gap:          '0px',
  },
  matchRowEven: {
    background: '#f7f8fa',
  },
  colDate: {
    width:      '140px',
    flexShrink: 0,
    fontFamily: 'monospace',
    fontSize:   '12px',
    color:      '#444',
  },
  colData: {
    width:      '80px',
    flexShrink: 0,
    textAlign:  'right',
    fontSize:   '12px',
  },

  tradeGroupLabel: {
    padding:      '5px 12px',
    fontSize:     '11px',
    fontWeight:   'bold',
    color:        '#2e7d32',
    background:   '#f0f7f0',
    borderBottom: '1px solid #e0e0e0',
  },
  colType: {
    width:      '60px',
    flexShrink: 0,
    textAlign:  'center',
    fontSize:   '11px',
    fontWeight: 'bold',
  },

  // ── R2 banners ──
  warnBanner: {
    margin:       '8px 16px 0 16px',
    padding:      '8px 12px',
    background:   '#fff8e1',
    border:       '1px solid #ffe082',
    borderRadius: '6px',
    flexShrink:   0,
  },
  errBanner: {
    margin:       '8px 16px 0 16px',
    padding:      '8px 12px',
    background:   '#ffebee',
    border:       '1px solid #ef9a9a',
    borderRadius: '6px',
    flexShrink:   0,
  },
  infoBanner: {
    margin:       '8px 16px 0 16px',
    padding:      '6px 12px',
    background:   '#e3f2fd',
    border:       '1px solid #90caf9',
    borderRadius: '6px',
    fontSize:     '11px',
    color:        '#1565c0',
    flexShrink:   0,
  },
  bannerTitle: {
    fontSize:     '12px',
    fontWeight:   'bold',
    color:        '#131722',
    marginBottom: '3px',
  },
  bannerLine: {
    fontSize: '11px',
    color:    '#555',
    padding:  '1px 0',
  },
};

// ============================================================
// TRADE STATISTICS
// Computed from the UNCAPPED trade_seq ([exit_time, pnl%]) so
// numbers stay correct even when trade lists are capped at 500.
// ============================================================

function computeTradeStats(symbolResults) {
  // Merge all symbols' sequences, sorted chronologically
  const seq = symbolResults
    .flatMap(r => (r.trade_seq && r.trade_seq.length
      ? r.trade_seq
      : (r.closed_trades || []).map(t => [t.exit_bucket_ist, t.pnl_percent || 0])
    ))
    .filter(x => x && x.length === 2)
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])));

  const n = seq.length;
  if (n === 0) return null;

  let wins = 0, losses = 0;
  let grossWin = 0, grossLoss = 0;
  let sumPnl = 0;
  let equity = 0, peak = 0, maxDD = 0;
  let streak = 0, worstStreak = 0;

  seq.forEach(([, pnl]) => {
    const p = Number(pnl) || 0;
    sumPnl += p;
    if (p > 0) {
      wins += 1;
      grossWin += p;
      streak = 0;
    } else {
      losses += 1;
      grossLoss += Math.abs(p);
      streak += 1;
      if (streak > worstStreak) worstStreak = streak;
    }
    equity += p;
    if (equity > peak) peak = equity;
    const dd = peak - equity;
    if (dd > maxDD) maxDD = dd;
  });

  const r2 = (x) => Math.round(x * 100) / 100;
  return {
    total:        n,
    wins,
    losses,
    winRate:      r2((wins / n) * 100),
    avgWin:       wins   > 0 ? r2(grossWin / wins)    : 0,
    avgLoss:      losses > 0 ? r2(grossLoss / losses) : 0,
    profitFactor: grossLoss > 0 ? r2(grossWin / grossLoss) : (grossWin > 0 ? Infinity : 0),
    expectancy:   r2(sumPnl / n),
    totalPnl:     r2(sumPnl),
    maxDrawdown:  r2(maxDD),
    worstStreak,
  };
}

function StatCell({ label, value, color }) {
  return (
    <div style={st.cell}>
      <span style={{ ...st.value, ...(color ? { color } : {}) }}>{value}</span>
      <span style={st.label}>{label}</span>
    </div>
  );
}

function TradeStatsBlock({ symbolResults }) {
  const stats = computeTradeStats(symbolResults);
  if (!stats) return null;

  const pf = stats.profitFactor === Infinity ? '∞' : stats.profitFactor;
  const posNeg = (v) => (v > 0 ? '#2e7d32' : v < 0 ? '#d32f2f' : '#555');

  return (
    <div style={st.box}>
      <div style={st.title}>Trade Statistics (all closed trades, sum of per-trade %)</div>
      <div style={st.grid}>
        <StatCell label="Closed Trades"   value={stats.total} />
        <StatCell label="Win Rate"        value={`${stats.winRate}%`}
                  color={stats.winRate >= 50 ? '#2e7d32' : '#e65100'} />
        <StatCell label="Wins / Losses"   value={`${stats.wins} / ${stats.losses}`} />
        <StatCell label="Avg Win"         value={`+${stats.avgWin}%`}  color="#2e7d32" />
        <StatCell label="Avg Loss"        value={`-${stats.avgLoss}%`} color="#d32f2f" />
        <StatCell label="Profit Factor"   value={pf}
                  color={stats.profitFactor >= 1 ? '#2e7d32' : '#d32f2f'} />
        <StatCell label="Expectancy / Trade" value={`${stats.expectancy > 0 ? '+' : ''}${stats.expectancy}%`}
                  color={posNeg(stats.expectancy)} />
        <StatCell label="Total P&L (Σ%)"  value={`${stats.totalPnl > 0 ? '+' : ''}${stats.totalPnl}%`}
                  color={posNeg(stats.totalPnl)} />
        <StatCell label="Max Drawdown"    value={`-${stats.maxDrawdown}%`} color="#d32f2f" />
        <StatCell label="Longest Losing Streak" value={stats.worstStreak} />
      </div>
    </div>
  );
}

const st = {
  box: {
    margin:       '8px 16px',
    padding:      '10px 14px',
    background:   '#fff',
    border:       '1px solid #e0e0e0',
    borderRadius: '6px',
    flexShrink:   0,
  },
  title: {
    fontSize:     '11px',
    fontWeight:   'bold',
    color:        '#888',
    textTransform:'uppercase',
    letterSpacing:'0.5px',
    marginBottom: '8px',
  },
  grid: {
    display:  'flex',
    flexWrap: 'wrap',
    gap:      '18px',
  },
  cell: {
    display:       'flex',
    flexDirection: 'column',
    minWidth:      '90px',
  },
  value: {
    fontSize:   '15px',
    fontWeight: 'bold',
    color:      '#131722',
  },
  label: {
    fontSize: '10px',
    color:    '#888',
  },
};

// ============================================================
// REPORT RESULTS — main component
// ============================================================

export default function ReportResults({ results, onRunAgain, onNewRun }) {
  const [chartsLoading, setChartsLoading] = useState(false);
  const [chartsError,   setChartsError]   = useState(null);

  if (!results) return null;

  const {
    report_name,
    base_timeframe,
    from_date,
    to_date,
    symbols_scanned,
    run_at,
    summary = {},
  } = results;

  const {
    symbols_with_matches = 0,
    total_matches = 0,
    symbols_with_errors = [],
  } = summary;
  const symbolResults = results.results || [];
  const runWarnings   = results.warnings || [];
  const runTruncated  = !!results.truncated;

  // ── Trade statistics (when exit/SL defined) ───────────────
  const hasExit = symbolResults.some(r => r.has_exit);

  const totalClosed = symbolResults.reduce((sum, r) => sum + (r.total_closed || 0), 0);
  const totalOpen   = symbolResults.reduce((sum, r) => sum + (r.total_open   || 0), 0);

  const allClosedTrades = symbolResults.flatMap(r =>
    (r.closed_trades || []).map(t => ({ ...t, symbol: r.symbol }))
  );
  const avgPnl = allClosedTrades.length > 0
    ? Math.round(
        allClosedTrades.reduce((sum, t) => sum + (t.pnl_percent || 0), 0)
        / allClosedTrades.length * 100
      ) / 100
    : 0;

  // Sort — symbols with matches first, then alphabetical
  const sorted = [...symbolResults].sort((a, b) => {
    if (b.total_matches !== a.total_matches)
      return b.total_matches - a.total_matches;
    return a.symbol.localeCompare(b.symbol);
  });

  // ── Open in Charts ────────────────────────────────────────
  const handleOpenCharts = async () => {
    setChartsError(null);

    const hitSymbols = symbolResults
      .filter(r => r.total_matches > 0)
      .map(r => r.symbol);

    console.log('hitSymbols:', hitSymbols);  // ← add

    if (hitSymbols.length === 0) {
      setChartsError('No matches to show on chart.');
      return;
    }

    setChartsLoading(true);
    try {
      const markersBySymbol = {};

      symbolResults.forEach(symResult => {
        if (symResult.total_matches === 0) return;
        const markers = [];

        if (symResult.has_exit) {
          // ── Trade view — entry + exit markers ──
          (symResult.closed_trades || []).forEach(trade => {
            // Entry marker — green arrow up
            markers.push({
              pattern_time: trade.entry_bucket,
              direction:    results.trade_direction === 'sell' ? 'sell' : 'buy',
            });
            // Exit marker — red arrow down
            markers.push({
              pattern_time: trade.exit_bucket,
              direction:    trade.exit_type === 'SL_HIT' ? 'sl' : 'exit',
            });
          });
          // Open trades — entry marker only
          (symResult.open_trades || []).forEach(trade => {
            markers.push({
              pattern_time: trade.entry_bucket,
              direction:    results.trade_direction === 'sell' ? 'sell' : 'buy',
            });
          });
        } else {
          // ── Entry only view ──
          symResult.matches.forEach(m => {
            markers.push({
              pattern_time: m.bucket,
              direction:    results.trade_direction === 'sell' ? 'sell' : 'buy',
            });
          });
        }

        markersBySymbol[symResult.symbol] = markers;
      });

      console.log('markersBySymbol:', markersBySymbol);  // ← add
      const res = await createTempWatchlist(hitSymbols);
      console.log('createTempWatchlist res:', res);  // ← add

      // Detect EMAs used in report conditions
      const detectEMAs = (conditions = []) => {
        const emas = new Set();
        const walk = (cond) => {
          if (!cond) return;
          if (cond.type === 'n_wrapper' && cond.inner) { walk(cond.inner); return; }
          ['left', 'right'].forEach(side => {
            const s = cond[side];
            if (s?.variable === 'EMA' && s.params?.period) {
              emas.add(`ema_${s.params.period}`);
            }
          });
        };
        conditions.forEach(walk);
        return [...emas];
      };

      const def = results.definition || {};
      const allConds = [
        ...(def.entry?.conditions     || []),
        ...(def.exit?.conditions      || []),
        ...(def.stop_loss?.conditions || []),
      ];
      const usedEMAs = detectEMAs(allConds);

      // Build visibleEMAs — only turn on EMAs used in report
      const visibleEMAs = {
        ema_5: false, ema_9: false, ema_20: false,
        ema_50: false, ema_200: false,
      };
      usedEMAs.forEach(k => {
        if (k in visibleEMAs) visibleEMAs[k] = true;
      });

      sessionStorage.setItem('markersConfig', JSON.stringify({
        enabled:         true,
        fetchFn:         null,
        filters:         {},
        markersBySymbol,
      }));

      // Save chart settings separately
      sessionStorage.setItem('reportChartSettings', JSON.stringify({
        timeframe:   results.base_timeframe,
        visibleEMAs,
      }));

      const urlParams = new URLSearchParams({
        watchlist:   res.watchlist_id,
        vpTimeframe: results.base_timeframe,
      });
      console.log('opening URL:', `${window.location.origin}?${urlParams.toString()}`);  // ← add

      window.open(
        `${window.location.origin}?${urlParams.toString()}`,
        '_blank'
      );
    } catch (err) {
      console.error('Charts error:', err);  // ← add
      setChartsError('Failed to open charts. Try again.');
    } finally {
      setChartsLoading(false);
    }
  };

  // ── Excel export ──────────────────────────────────────────
  const handleExport = () => exportToExcel(results);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div style={s.container}>

      {/* ── Header ── */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.headerTitle}>Results — {report_name}</span>
          <span style={s.headerMeta}>
            {base_timeframe} · {from_date} → {to_date}
          </span>
        </div>
        <div style={s.headerActions}>
          <button style={s.btnSecondary} onClick={onRunAgain}>
            ← Run Again
          </button>
          <button style={s.btnSecondary} onClick={onNewRun}>
            ← Reports
          </button>
        </div>
      </div>

      {/* ── Summary bar ── */}
      <div style={s.summaryBar}>
        <div style={s.summaryItem}>
          <span style={s.summaryNum}>{symbols_scanned}</span>
          <span style={s.summaryLabel}>Symbols Scanned</span>
        </div>
        <div style={s.summaryDivider} />
        <div style={s.summaryItem}>
          <span style={{
            ...s.summaryNum,
            color: symbols_with_matches > 0 ? '#2e7d32' : '#aaa',
          }}>
            {symbols_with_matches}
          </span>
          <span style={s.summaryLabel}>With Matches</span>
        </div>
        <div style={s.summaryDivider} />
        <div style={s.summaryItem}>
          <span style={{
            ...s.summaryNum,
            color: total_matches > 0 ? '#2962ff' : '#aaa',
          }}>
            {total_matches}
          </span>
          <span style={s.summaryLabel}>Total Entries</span>
        </div>

        {/* Trade stats — only shown when exit/SL defined */}
        {hasExit && (
          <>
            <div style={s.summaryDivider} />
            <div style={s.summaryItem}>
              <span style={{
                ...s.summaryNum,
                color: totalClosed > 0 ? '#2e7d32' : '#aaa',
              }}>
                {totalClosed}
              </span>
              <span style={s.summaryLabel}>Closed Trades</span>
            </div>
            <div style={s.summaryDivider} />
            <div style={s.summaryItem}>
              <span style={{
                ...s.summaryNum,
                color: totalOpen > 0 ? '#e65100' : '#aaa',
              }}>
                {totalOpen}
              </span>
              <span style={s.summaryLabel}>Open Trades</span>
            </div>
            <div style={s.summaryDivider} />
            <div style={s.summaryItem}>
              <span style={{
                ...s.summaryNum,
                color: avgPnl > 0 ? '#2e7d32' : avgPnl < 0 ? '#d32f2f' : '#aaa',
              }}>
                {totalClosed > 0 ? `${avgPnl > 0 ? '+' : ''}${avgPnl}%` : '—'}
              </span>
              <span style={s.summaryLabel}>Avg P&L</span>
            </div>
          </>
        )}

        <div style={s.summaryDivider} />
        <div style={s.summaryItem}>
          <span style={s.summaryNum}>{run_at ? fmtBucket(run_at) : '—'}</span>
          <span style={s.summaryLabel}>Run At (UTC)</span>
        </div>

        {/* Action buttons in summary bar */}
        <div style={{
          ...s.summaryItem,
          paddingLeft: '16px',
        }}>
          <span style={{
            fontSize:        '13px',
            fontWeight:      'bold',
            color:           results.trade_direction === 'sell' ? '#d32f2f' : '#2e7d32',
            backgroundColor: results.trade_direction === 'sell' ? '#ffebee' : '#e8f5e9',
            padding:         '4px 10px',
            borderRadius:    '12px',
            border:          `1px solid ${results.trade_direction === 'sell' ? '#ffcdd2' : '#c8e6c9'}`,
          }}>
            {results.trade_direction === 'sell' ? '📉 Sell' : '📈 Buy'}
          </span>
        </div>

        <div style={s.summaryActions}>
          {total_matches > 0 && (
            <>
              <button
                style={{ ...s.btnCharts, ...(chartsLoading ? s.btnDisabled : {}) }}
                onClick={handleOpenCharts}
                disabled={chartsLoading}
              >
                {chartsLoading ? '...' : '📈 Charts'}
              </button>
              <button style={s.btnExport} onClick={handleExport}>
                ⬇ Excel
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Charts error ── */}
      {chartsError && (
        <div style={s.chartsError}>
          {chartsError}
          <button
            style={s.msgClose}
            onClick={() => setChartsError(null)}
          >✕</button>
        </div>
      )}

      {/* ── No matches message ── */}
      {total_matches === 0 && (
        <div style={s.noMatches}>
          No candles matched all conditions in the selected date range and timeframe.
          Try a wider date range or different timeframe.
        </div>
      )}

      {/* ── Run warnings — skipped conditions etc. (from R1) ── */}
      {runWarnings.length > 0 && (
        <div style={s.warnBanner}>
          <div style={s.bannerTitle}>⚠ Warnings — results may be affected</div>
          {runWarnings.map((w, i) => (
            <div key={i} style={s.bannerLine}>{w}</div>
          ))}
        </div>
      )}

      {/* ── Failed symbols ── */}
      {symbols_with_errors.length > 0 && (
        <div style={s.errBanner}>
          <div style={s.bannerTitle}>
            ✕ {symbols_with_errors.length} symbol
            {symbols_with_errors.length > 1 ? 's' : ''} failed to scan
            — their "No matches" is NOT a verdict
          </div>
          {symbols_with_errors.map((e, i) => (
            <div key={i} style={s.bannerLine}>
              <strong>{e.symbol}</strong>: {e.error}
            </div>
          ))}
        </div>
      )}

      {/* ── Truncation notice ── */}
      {runTruncated && (
        <div style={s.infoBanner}>
          Large result — match/trade lists are capped at 500 per symbol
          (most recent kept). Counts and statistics reflect the FULL totals.
        </div>
      )}

      {/* ── Trade statistics ── */}
      {hasExit && totalClosed > 0 && (
        <TradeStatsBlock symbolResults={symbolResults} />
      )}

      {/* ── Results table ── */}
      <div style={s.tableWrap}>

        {/* Table header */}
        <div style={s.tableHeader}>
          <span style={s.thExpand} />
          <span style={s.thSymbol}>Symbol</span>
          <span style={s.thMatches}>Matches</span>
          <span style={s.thScanned}>Scanned</span>
          <span style={s.thLatest}>Latest Match</span>
        </div>

        {/* Symbol rows */}
        <div style={s.tableBody}>
          {sorted.map(symResult => (
            <SymbolResultRow
              key={symResult.symbol}
              symResult={symResult}
              timeframe={base_timeframe}
              reportId={results?.definition?.report_id}
            />
          ))}
        </div>
      </div>

    </div>
  );
}


// ============================================================
// STYLES
// ============================================================
const s = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100%',
    overflow:      'hidden',
    background:    '#f7f8fa',
  },
  header: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '10px 16px',
    background:     '#fff',
    borderBottom:   '1px solid #e0e0e0',
    flexShrink:     0,
  },
  headerLeft: {
    display:    'flex',
    alignItems: 'baseline',
    gap:        '10px',
    flexWrap:   'wrap',
  },
  headerTitle: {
    fontSize:   '14px',
    fontWeight: 'bold',
    color:      '#131722',
  },
  headerMeta: {
    fontSize: '12px',
    color:    '#888',
  },
  headerActions: {
    display: 'flex',
    gap:     '6px',
  },
  summaryBar: {
    display:     'flex',
    alignItems:  'center',
    gap:         '0px',
    padding:     '10px 16px',
    background:  '#fff',
    borderBottom:'1px solid #e0e0e0',
    flexShrink:  0,
    flexWrap:    'wrap',
    gap:         '0',
  },
  summaryItem: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    padding:       '0 20px',
  },
  summaryNum: {
    fontSize:   '20px',
    fontWeight: 'bold',
    color:      '#131722',
    lineHeight: '1.2',
  },
  summaryLabel: {
    fontSize: '10px',
    color:    '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  summaryDivider: {
    width:      '1px',
    height:     '32px',
    background: '#e0e0e0',
  },
  summaryActions: {
    display:    'flex',
    alignItems: 'center',
    gap:        '8px',
    marginLeft: 'auto',
  },
  noMatches: {
    padding:    '20px 16px',
    fontSize:   '13px',
    color:      '#888',
    background: '#fff',
    borderBottom: '1px solid #e0e0e0',
    flexShrink: 0,
  },
  tableWrap: {
    flex:      1,
    overflowY: 'auto',
    background:'#fff',
  },
  tableHeader: {
    display:      'flex',
    alignItems:   'center',
    gap:          '10px',
    padding:      '6px 12px',
    background:   '#f7f8fa',
    borderBottom: '2px solid #e0e0e0',
    fontSize:     '10px',
    fontWeight:   'bold',
    color:        '#888',
    textTransform:'uppercase',
    letterSpacing:'0.4px',
    position:     'sticky',
    top:          0,
    zIndex:       1,
  },
  thExpand:  { width: '12px', flexShrink: 0 },
  thSymbol:  { width: '110px', flexShrink: 0 },
  thMatches: { width: '100px', flexShrink: 0 },
  thScanned: { flex: 1 },
  thLatest:  { marginLeft: 'auto', textAlign: 'right' },
  tableBody: {
    display:       'flex',
    flexDirection: 'column',
  },
  btnCharts: {
    padding:         '5px 12px',
    fontSize:        '12px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  btnExport: {
    padding:         '5px 12px',
    fontSize:        '12px',
    backgroundColor: '#f0f0f0',
    color:           '#444',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
  },
  btnSecondary: {
    padding:         '6px 12px',
    fontSize:        '12px',
    backgroundColor: '#f0f0f0',
    color:           '#444',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
  },
  btnDisabled: {
    backgroundColor: '#bbb',
    cursor:          'not-allowed',
  },
  chartsError: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         '8px 16px',
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    borderBottom:    '1px solid #ffcdd2',
    fontSize:        '12px',
    flexShrink:      0,
  },
  msgClose: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '14px',
    color:      'inherit',
  },
};